/**
 * cartStore — "Pedido abierto" del vendedor (carrito en el navegador).
 *
 * Acumula productos para armar una cotización/pedido sin salir del catálogo.
 * Persistido en localStorage (por-origen = por-tenant, ya que cada tenant es un
 * subdominio). Se convierte en un SalesOrder real al "Ir al pedido" (el formulario
 * de pedido se precarga con estas líneas y ahí se elige/crea el cliente).
 */
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

export interface CartItem {
  product_id: string
  sku: string
  name: string
  unit: string
  unit_price: string      // neto unitario, como string (igual que el catálogo)
  price_currency: string
  is_tax_exempt: boolean
  quantity: number
}

/** Producto mínimo aceptado por addItem (compatible con ProductListItem). */
export interface CartAddable {
  id: string
  sku: string
  name: string
  unit: string
  unit_price: string
  price_currency?: string
  is_tax_exempt?: boolean
}

interface CartState {
  items: CartItem[]
  addItem: (p: CartAddable, qty?: number) => void
  setQty: (productId: string, qty: number) => void
  removeItem: (productId: string) => void
  clear: () => void
}

export const useCartStore = create<CartState>()(
  persist(
    (set) => ({
      items: [],
      addItem: (p, qty = 1) =>
        set((state) => {
          const existing = state.items.find((i) => i.product_id === p.id)
          if (existing) {
            return {
              items: state.items.map((i) =>
                i.product_id === p.id ? { ...i, quantity: i.quantity + qty } : i,
              ),
            }
          }
          return {
            items: [
              ...state.items,
              {
                product_id: p.id,
                sku: p.sku,
                name: p.name,
                unit: p.unit || 'UN',
                unit_price: p.unit_price ?? '0',
                price_currency: p.price_currency || 'CLP',
                is_tax_exempt: !!p.is_tax_exempt,
                quantity: qty,
              },
            ],
          }
        }),
      setQty: (productId, qty) =>
        set((state) => ({
          items:
            qty <= 0
              ? state.items.filter((i) => i.product_id !== productId)
              : state.items.map((i) =>
                  i.product_id === productId ? { ...i, quantity: qty } : i,
                ),
        })),
      removeItem: (productId) =>
        set((state) => ({ items: state.items.filter((i) => i.product_id !== productId) })),
      clear: () => set({ items: [] }),
    }),
    { name: 'zen-cart', storage: createJSONStorage(() => localStorage) },
  ),
)

/** Total de unidades en el carrito (para el badge). */
export function cartItemCount(items: CartItem[]): number {
  return items.reduce((n, i) => n + i.quantity, 0)
}

/** Totales del carrito (neto, IVA, total). taxRate en % (ej. 19). El IVA solo
 *  aplica a líneas no exentas. Se asume moneda CLP (el caso del mostrador). */
export function cartTotals(items: CartItem[], taxRate: number) {
  let neto = 0
  let iva = 0
  for (const i of items) {
    const line = parseFloat(i.unit_price || '0') * i.quantity
    neto += line
    if (!i.is_tax_exempt) iva += line * (taxRate / 100)
  }
  neto = Math.round(neto)
  iva = Math.round(iva)
  return { neto, iva, total: neto + iva }
}

/** Mapea el carrito a las líneas que espera el formulario de pedido (OrderLine). */
export function cartToOrderLines(items: CartItem[]) {
  return items.map((i) => ({
    sku: i.sku,
    name: i.name,
    description: '',
    unit: i.unit,
    quantity: String(i.quantity),
    unit_price: i.unit_price,
    price_currency: i.price_currency,
    discount_pct: '0',
    is_tax_exempt: i.is_tax_exempt,
    commission_rate: '0',
    tax_data: {},
  }))
}
